/**
 * The words a brand's console speaks (docs/studio-console-redesign.md, section 1). One data model
 * (titles, series, live events...) under every brand; each kind of business names those things in
 * its own words, so a church sees Messages and Services where a gym sees Classes and Live classes.
 * Screens ask this module for a word and never print the model's own names.
 *
 * Pure: data and lookups, no imports at run time (the unit tests run it under plain Node).
 */

import type { EntityType } from "../api/admin";

export interface Noun {
  one: string;
  many: string;
}

export type Vertical = "fitness" | "film" | "faith" | "sport" | "education" | "kids" | "music" | "food" | "theatre" | "events";

export interface Pack {
  vertical: Vertical | "general";
  /** The business's word for each kind of item. `page` is a screen of the app. */
  nouns: Record<EntityType, Noun>;
  /** Stages (rooms, pitches, streams of an event) are shown only where the business has them. */
  showsStages: boolean;
  /** The kind a new title gets (the feed's title.kind); the kind select lives under Advanced. */
  defaultTitleKind: string;
  /** Library sub-items shown even while empty, in sidebar order (others appear once they have items). */
  library: EntityType[];
}

const n = (one: string, many: string): Noun => ({ one, many });

/** The same in every pack. */
const COLLECTION = n("Collection", "Collections");
const SCREEN = n("Screen", "Screens");
const STAGE = n("Stage", "Stages");

const PACKS: Record<Vertical, Pack> = {
  fitness: {
    vertical: "fitness",
    nouns: { title: n("Class", "Classes"), series: n("Programme", "Programmes"), liveEvent: n("Live class", "Live classes"), person: n("Instructor", "Instructors"), collection: COLLECTION, taxonomy: n("Category", "Categories"), stage: STAGE, page: SCREEN },
    showsStages: false, defaultTitleKind: "class", library: ["title", "series", "collection", "person", "taxonomy"],
  },
  film: {
    vertical: "film",
    nouns: { title: n("Film", "Films"), series: n("Series", "Series"), liveEvent: n("Screening", "Screenings"), person: n("Person", "People"), collection: COLLECTION, taxonomy: n("Genre", "Genres"), stage: STAGE, page: SCREEN },
    showsStages: false, defaultTitleKind: "film", library: ["title", "series", "collection", "person", "taxonomy"],
  },
  faith: {
    vertical: "faith",
    nouns: { title: n("Message", "Messages"), series: n("Series", "Series"), liveEvent: n("Service", "Services"), person: n("Speaker", "Speakers"), collection: n("Playlist", "Playlists"), taxonomy: n("Topic", "Topics"), stage: STAGE, page: SCREEN },
    showsStages: false, defaultTitleKind: "sermon", library: ["title", "series", "collection", "person", "taxonomy"],
  },
  sport: {
    vertical: "sport",
    nouns: { title: n("Video", "Videos"), series: n("Series", "Series"), liveEvent: n("Match", "Matches"), person: n("Player", "Players"), collection: COLLECTION, taxonomy: n("Competition", "Competitions"), stage: STAGE, page: SCREEN },
    showsStages: false, defaultTitleKind: "match", library: ["title", "collection", "person", "taxonomy"],
  },
  education: {
    vertical: "education",
    nouns: { title: n("Lesson", "Lessons"), series: n("Course", "Courses"), liveEvent: n("Live session", "Live sessions"), person: n("Teacher", "Teachers"), collection: COLLECTION, taxonomy: n("Topic", "Topics"), stage: STAGE, page: SCREEN },
    showsStages: false, defaultTitleKind: "lesson", library: ["title", "series", "collection", "person", "taxonomy"],
  },
  kids: {
    vertical: "kids",
    nouns: { title: n("Episode", "Episodes"), series: n("Show", "Shows"), liveEvent: n("Live show", "Live shows"), person: n("Character", "Characters"), collection: COLLECTION, taxonomy: n("Age group", "Age groups"), stage: STAGE, page: SCREEN },
    showsStages: false, defaultTitleKind: "episode", library: ["title", "series", "collection", "person", "taxonomy"],
  },
  music: {
    vertical: "music",
    nouns: { title: n("Recording", "Recordings"), series: n("Series", "Series"), liveEvent: n("Concert", "Concerts"), person: n("Artist", "Artists"), collection: COLLECTION, taxonomy: n("Genre", "Genres"), stage: STAGE, page: SCREEN },
    showsStages: true, defaultTitleKind: "concert", library: ["title", "collection", "person", "taxonomy", "stage"],
  },
  food: {
    vertical: "food",
    nouns: { title: n("Recipe", "Recipes"), series: n("Series", "Series"), liveEvent: n("Cook-along", "Cook-alongs"), person: n("Chef", "Chefs"), collection: COLLECTION, taxonomy: n("Cuisine", "Cuisines"), stage: STAGE, page: SCREEN },
    showsStages: false, defaultTitleKind: "recipe", library: ["title", "series", "collection", "person", "taxonomy"],
  },
  theatre: {
    vertical: "theatre",
    nouns: { title: n("Performance", "Performances"), series: n("Season", "Seasons"), liveEvent: n("Live performance", "Live performances"), person: n("Cast & creative", "Cast & creatives"), collection: COLLECTION, taxonomy: n("Genre", "Genres"), stage: STAGE, page: SCREEN },
    showsStages: true, defaultTitleKind: "performance", library: ["title", "series", "collection", "person", "taxonomy", "stage"],
  },
  events: {
    vertical: "events",
    nouns: { title: n("Session", "Sessions"), series: n("Track", "Tracks"), liveEvent: n("Live session", "Live sessions"), person: n("Speaker", "Speakers"), collection: COLLECTION, taxonomy: n("Topic", "Topics"), stage: STAGE, page: SCREEN },
    showsStages: true, defaultTitleKind: "session", library: ["title", "series", "collection", "person", "taxonomy", "stage"],
  },
};

/** For a deployment without a known vertical: plain words that fit any video business. */
const GENERAL: Pack = {
  vertical: "general",
  nouns: { title: n("Video", "Videos"), series: n("Series", "Series"), liveEvent: n("Live event", "Live events"), person: n("Person", "People"), collection: COLLECTION, taxonomy: n("Category", "Categories"), stage: STAGE, page: SCREEN },
  showsStages: false, defaultTitleKind: "film", library: ["title", "series", "collection", "person", "taxonomy"],
};

export const VERTICALS = Object.keys(PACKS) as Vertical[];

export function packFor(vertical: string | null | undefined): Pack {
  return (vertical && (PACKS as Record<string, Pack>)[vertical]) || GENERAL;
}

export function noun(pack: Pack, type: EntityType, count: number | "one" | "many" = "one"): string {
  const word = pack.nouns[type];
  return count === "one" || count === 1 ? word.one : word.many;
}

/** "1 class", "3 classes": a count with the business's word, in lower case. */
export function countOf(pack: Pack, type: EntityType, count: number): string {
  return `${count} ${lower(noun(pack, type, count))}`;
}

/** Lower case for use inside a sentence ("New class"); words that are names keep their capitals. */
export function lower(word: string): string {
  return word.toLowerCase();
}

/**
 * Library sub-items for a brand: the pack's own list, plus any other kind that already has items
 * (a film brand that added a stage still sees it). Live events and screens live in their own
 * sections; stages only where the pack shows them or the brand already uses them.
 */
export function libraryTypes(pack: Pack, counts: Partial<Record<EntityType, number>>): EntityType[] {
  const order: EntityType[] = ["title", "series", "collection", "person", "taxonomy", "stage"];
  return order.filter((t) => pack.library.includes(t) || (counts[t] ?? 0) > 0);
}

/**
 * The words that replace the model's jargon on every screen (section 1): never "entitlement",
 * "grants", "feed", "rails", "taxonomy" or a publish version.
 */
export const TERMS = {
  entitlement: "Who can watch",
  grants: "Codes & guest lists",
  feed: "your app",
  rails: "rows",
  rail: "row",
} as const;
